import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, IconButton, Select, Option, Stack, Tooltip, Slider, Typography, Switch, CircularProgress } from '@mui/joy'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import ttsService from '../../utils/tts.service'
import SettingsIcon from '@mui/icons-material/Settings'
import CloseIcon from '@mui/icons-material/Close'
import { useSegmentedSpeech } from '../../hooks/useSegmentedSpeech'
import { languageOptions as deviceLanguages, resolveVoice as coreResolveVoice } from '@nowry/core/domain/voiceMatch'

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * The device's languages, each with a display name.
 *
 * Which language a voice speaks, which of several voices is the good one, and
 * what to do with Android's `ja_JP` are all `voiceMatch`'s rules now — the
 * phone answers the same questions and a second copy of them drifted the first
 * time it was written. What stays here is the label, because naming a language
 * to a reader is a browser question: `Intl.DisplayNames` is not on every
 * runtime that package serves.
 */
function buildLanguageOptions(voices) {
  return (
    deviceLanguages(voices)
      .map((entry) => {
        let label = entry.langBase
        try {
          label = new Intl.DisplayNames([navigator.language || 'en'], { type: 'language' }).of(entry.langBase) || entry.langBase
        } catch (_) {
          label = entry.langBase
        }
        return { ...entry, label }
      })
      /* Sorted by the NAME the reader sees, not by the code underneath it — the
       shared list is ordered by code, which is the only order available to a
       module that cannot name a language. */
      .sort((a, b) => a.label.localeCompare(b.label))
  )
}

/** The saved voice, resolved against this device (see `voiceMatch`). */
function resolveVoice(voices, { targetLang, targetName }) {
  return coreResolveVoice(voices, { targetLang, targetName }, ttsService.getVoices())
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TTSControls({
  text,
  compact = false,
  settingsOnly = false,
  settingsOpen,
  onSettingsChange,
  voiceSettings,
  onVoiceSettingsChange,
  langHint = null, // the card's language for kanji alone (ADR-050)
  onPlay = null // told when the learner starts playback (ADR-059 §2)
}) {
  const { t } = useTranslation()
  const { getSegments, speakSegments } = useSegmentedSpeech()

  const [isPlaying, setIsPlaying] = useState(false)
  const [allVoices, setAllVoices] = useState([]) // raw list from Web Speech API
  const [selectedLang, setSelectedLang] = useState(null) // e.g. "ja"
  const [selectedVoice, setSelectedVoice] = useState(null) // actual SpeechSynthesisVoice
  const [rate, setRate] = useState(1.0)
  const [autoPlay, setAutoPlay] = useState(false)
  const [mode, setMode] = useState('auto') // 'auto' | 'manual' — auto-detect language per segment (ADR-001)
  const [isSegmenting, setIsSegmenting] = useState(false) // true while getSegments() is in flight on a cache miss
  const [internalShowSettings, setInternalShowSettings] = useState(false)
  // Tracks whether the user has ever initiated playback – required for auto‑play on mobile browsers
  const [userInitiated, setUserInitiated] = useState(false)

  const isSettingsOpen = settingsOpen !== undefined ? settingsOpen : internalShowSettings
  const setSettingsOpen = onSettingsChange || setInternalShowSettings

  // ── Language options derived from available voices ─────────────────────────
  // Each device contributes whatever TTS voices are installed; we normalize them
  // into a clean language list that is identical in structure across all devices.
  const languageOptions = useMemo(() => buildLanguageOptions(allVoices), [allVoices])

  // ── Load voices ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleVoices = (voices) => setAllVoices(voices)
    const cleanup = ttsService.onVoicesReady(handleVoices)
    return cleanup
  }, [])

  // ── Sync auto/manual mode from saved settings ──────────────────────────────
  // Independent of allVoices (unlike the effect below) so the toggle reflects
  // the saved mode immediately, without waiting for voices to load. Uses the
  // same backward-compat derivation rule as normalizeSide() in
  // useVoiceSettings.js in case voiceSettings arrives un-normalized here.
  useEffect(() => {
    if (!voiceSettings) return
    const targetLang = voiceSettings.voiceLang || voiceSettings.voice_lang || null
    setMode(voiceSettings.mode || (targetLang ? 'manual' : 'auto'))
  }, [voiceSettings])

  // ── Apply saved settings whenever voices or settings change ───────────────
  // Uses language code as the canonical key, so the correct voice is found
  // regardless of which TTS engine or device is in use.
  useEffect(() => {
    if (!allVoices.length) return

    if (voiceSettings) {
      const targetLang = voiceSettings.voiceLang || voiceSettings.voice_lang || null
      const targetName = voiceSettings.voiceName || voiceSettings.voice_name || null

      const matched = resolveVoice(allVoices, { targetLang, targetName })
      if (matched) {
        const langBase = matched.lang.replace(/_/g, '-').split('-')[0].toLowerCase()
        setSelectedLang(langBase)
        setSelectedVoice(matched)
        ttsService.setVoice(matched)
      }

      if (voiceSettings.rate !== undefined) setRate(voiceSettings.rate)
      if (voiceSettings.autoPlay !== undefined) setAutoPlay(voiceSettings.autoPlay)
      else if (voiceSettings.auto_play !== undefined) setAutoPlay(voiceSettings.auto_play)
    } else if (!selectedVoice) {
      // No settings saved — default to first English voice
      const def = resolveVoice(allVoices, { targetLang: 'en', targetName: null })
      if (def) {
        setSelectedLang('en')
        setSelectedVoice(def)
        ttsService.setVoice(def)
      }
    }
  }, [voiceSettings, allVoices, selectedVoice, onVoiceSettingsChange])

  // ── Handlers ───────────────────────────────────────────────────────────────
  // Which play is current. A card change bumps it, so segments fetched for the
  // card the learner already left are never spoken over the next one.
  const playRun = React.useRef(0)
  // The text last started, by a tap or by auto-play: auto-play skips it, so the
  // first tap — which is also what unlocks auto-play — speaks once, not twice.
  const startedText = React.useRef(null)

  /** Speak `text` the one way both Listen and auto-play use (TTS-004). */
  const playText = React.useCallback(async () => {
    if (!text) return
    const run = ++playRun.current
    startedText.current = text

    const playbackCallbacks = {
      rate,
      onStart: () => setIsPlaying(true),
      onEnd: () => setIsPlaying(false),
      onError: () => setIsPlaying(false)
    }

    if (mode === 'auto') {
      setIsSegmenting(true)
      const segments = await getSegments(text, langHint)
      setIsSegmenting(false)
      if (run !== playRun.current) return
      if (segments) {
        speakSegments(segments, playbackCallbacks)
        return
      }
      // getSegments() failed (offline/rate-limited/backend error) — silent
      // fallback to the existing single-utterance path below, no error UI.
    }

    ttsService.speak(text, playbackCallbacks)
  }, [text, langHint, rate, mode, getSegments, speakSegments])

  const handlePlay = React.useCallback(() => {
    // Mark that the user has initiated playback – this satisfies the user‑gesture requirement on mobile
    setUserInitiated(true)
    onPlay?.()
    return playText()
  }, [playText, onPlay])

  const handlePause = () => {
    ttsService.pause()
    setIsPlaying(false)
  }

  // ── Auto-play effect ───────────────────────────────────────────────────────
  // Intentional dep-array choices:
  //   • `isPlaying` is excluded — including it caused the effect to re-run
  //     every time speech ended (setIsPlaying(false)), immediately re-triggering
  //     playback and creating an infinite play loop.
  //   • `playText` is excluded — it changes identity with `text`; including
  //     it caused the effect to fire TWICE per card change (once for `text`, once
  //     for the new reference), producing double-play.
  //   • Cleanup cancels any in-progress speech, and any segmentation still in
  //     flight, before the next card starts.
  useEffect(() => {
    // Auto‑play is only allowed after a user interaction (required on iOS/Android)
    if (!autoPlay || !text || allVoices.length === 0 || !userInitiated) return
    if (startedText.current === text) return
    playText()
    return () => {
      playRun.current += 1
      startedText.current = null
      ttsService.stop()
      setIsPlaying(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, autoPlay, allVoices.length, userInitiated, rate])

  const handleLanguageChange = (event, langBase) => {
    if (!langBase) return
    const entry = languageOptions.find((o) => o.langBase === langBase)
    if (!entry) return

    const voice = entry.bestVoice
    setSelectedLang(langBase)
    setSelectedVoice(voice)
    ttsService.setVoice(voice)

    // Save lang code + name so both can be used for matching on any future device
    if (onVoiceSettingsChange) {
      onVoiceSettingsChange({ voiceName: voice.name, voiceLang: voice.lang, rate, pitch: 1.0, autoPlay })
    }
  }

  const handleRateChange = (e, val) => {
    setRate(val)
    if (onVoiceSettingsChange) {
      onVoiceSettingsChange({ voiceName: selectedVoice?.name, voiceLang: selectedVoice?.lang, rate: val, pitch: 1.0, autoPlay })
    }
  }

  const handleAutoPlayChange = (e) => {
    const newVal = e.target.checked
    setAutoPlay(newVal)
    // Enabling auto‑play is a user gesture, so mark interaction
    if (newVal) setUserInitiated(true)
    if (onVoiceSettingsChange) {
      onVoiceSettingsChange({ voiceName: selectedVoice?.name, voiceLang: selectedVoice?.lang, rate, pitch: 1.0, autoPlay: newVal })
    }
  }

  const handleAutoDetectChange = (e) => {
    const newVal = e.target.checked ? 'auto' : 'manual'
    setMode(newVal)
    if (onVoiceSettingsChange) {
      onVoiceSettingsChange({ voiceName: selectedVoice?.name, voiceLang: selectedVoice?.lang, rate, pitch: 1.0, autoPlay, mode: newVal })
    }
  }

  // ── Click outside ──────────────────────────────────────────────────────────
  const settingsRef = React.useRef(null)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isPortalClick =
        event.target.closest('[role="presentation"]') ||
        event.target.closest('[role="listbox"]') ||
        event.target.closest('.MuiPopover-root') ||
        event.target.closest('[data-mui-portal]')
      if (isPortalClick) return
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false)
      }
    }
    if (isSettingsOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSettingsOpen, setSettingsOpen])

  // ── Settings-only render (used by CardPreviewModal to show panel without floating buttons) ──
  if (settingsOnly) {
    return (
      <Stack spacing={2}>
        {/* Auto-detect language toggle -- visible in both modes, this is how you switch INTO manual mode */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography level='body-xs' sx={{ fontWeight: 600 }}>
            {t('tts.autoDetect')}
          </Typography>
          <Switch size='sm' checked={mode === 'auto'} onChange={handleAutoDetectChange} aria-label={t('tts.autoDetectAriaLabel')} />
        </Stack>

        {/* Language selector -- manual mode only */}
        {mode === 'manual' && (
          <Box>
            <Typography level='body-xs' sx={{ mb: 0.5, fontWeight: 600 }}>
              Language
            </Typography>
            <Select
              size='sm'
              value={selectedLang ?? null}
              onChange={handleLanguageChange}
              placeholder={languageOptions.length === 0 ? 'Loading\u2026' : 'Select Language'}
            >
              {languageOptions.map((opt) => (
                <Option key={opt.langBase} value={opt.langBase}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Box>
        )}

        {/* Speed slider */}
        <Box>
          <Typography level='body-xs' sx={{ mb: 0.5, fontWeight: 600 }}>
            Speed: {rate}x
          </Typography>
          <Slider value={rate} onChange={handleRateChange} min={0.5} max={2.0} step={0.1} size='sm' />
        </Box>

        {/* Auto-play toggle */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography level='body-xs' sx={{ fontWeight: 600 }}>
            Auto-Play
          </Typography>
          <Switch size='sm' checked={autoPlay} onChange={handleAutoPlayChange} />
        </Stack>
      </Stack>
    )
  }

  // ── Compact render (floating over a card) ─────────────────────────────────
  if (compact) {
    return (
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'flex', gap: 1 }}>
        <Tooltip title={isPlaying ? 'Pause' : 'Listen'}>
          <IconButton
            size='sm'
            variant='solid'
            color='primary'
            onClick={isPlaying ? handlePause : handlePlay}
            disabled={!text || isSegmenting}
            sx={{ borderRadius: '50%', boxShadow: 'sm', minHeight: { xs: 44, md: 32 }, minWidth: { xs: 44, md: 32 } }}
          >
            {isSegmenting ? (
              <CircularProgress size='sm' sx={{ '--CircularProgress-size': '18px' }} />
            ) : isPlaying ? (
              <PauseIcon />
            ) : (
              <PlayArrowIcon />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title='Voice Settings'>
          <IconButton
            size='sm'
            onMouseDown={(e) => e.stopPropagation()}
            variant='soft'
            color='neutral'
            onClick={() => setSettingsOpen(!isSettingsOpen)}
            sx={{ borderRadius: '50%', minHeight: { xs: 44, md: 32 }, minWidth: { xs: 44, md: 32 } }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>

        {isSettingsOpen && (
          <Box
            ref={settingsRef}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 40,
              right: 0,
              width: 280,
              p: 2,
              bgcolor: 'background.surface',
              borderRadius: 'md',
              boxShadow: 'lg',
              border: '1px solid',
              borderColor: 'neutral.outlinedBorder',
              zIndex: 20
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
              <Typography level='title-sm'>Voice Settings</Typography>
              <IconButton
                size='sm'
                variant='plain'
                color='neutral'
                onClick={() => setSettingsOpen(false)}
                sx={{ minHeight: { xs: 44, md: 32 }, minWidth: { xs: 44, md: 32 } }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </Stack>

            <Stack spacing={2}>
              {/* Auto-detect language toggle -- visible in both modes, this is how you switch INTO manual mode */}
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography level='body-xs' sx={{ fontWeight: 600 }}>
                  {t('tts.autoDetect')}
                </Typography>
                <Switch size='sm' checked={mode === 'auto'} onChange={handleAutoDetectChange} aria-label={t('tts.autoDetectAriaLabel')} />
              </Stack>

              {mode === 'manual' && (
                <Box>
                  <Typography level='body-xs' sx={{ mb: 0.5, fontWeight: 600 }}>
                    Language
                  </Typography>
                  <Select
                    size='sm'
                    value={selectedLang ?? null}
                    onChange={handleLanguageChange}
                    placeholder={languageOptions.length === 0 ? 'Loading\u2026' : 'Select Language'}
                  >
                    {languageOptions.map((opt) => (
                      <Option key={opt.langBase} value={opt.langBase}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Box>
              )}

              <Box>
                <Typography level='body-xs' sx={{ mb: 0.5, fontWeight: 600 }}>
                  Speed: {rate}x
                </Typography>
                <Slider value={rate} onChange={handleRateChange} min={0.5} max={2.0} step={0.1} size='sm' />
              </Box>

              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography level='body-xs' sx={{ fontWeight: 600 }}>
                  Auto-Play
                </Typography>
                <Switch size='sm' checked={autoPlay} onChange={handleAutoPlayChange} />
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    )
  }

  return null
}
